import { defineStore } from '#imports';

import type { IArticle, TFetchArticlesPayload } from './types';
import type { Pagination } from '@/shared/types/pagination';

import articleService from './api';
import findDepthFirstArticle from './utils/findDepthFirstArticle';
import { FallbackArticle } from './constant';

interface State {
	articles: Pagination<any> | null;
	openArticleId: number | null;
	pureArticles: Array<IArticle>;
	articleStorage: Map<number, IArticle>;
}

export const useArticlesStore = defineStore('Articles', {
	state: (): State => ({
		articles: null,
		openArticleId: null,
		pureArticles: [],
		articleStorage: new Map(),
	}),

	getters: {
		articleById: ({ articles }) => (id: IArticle['id'], type: IArticle['type']) => {
			return findDepthFirstArticle((articles?.results ?? []), id, type);
		},

		isEmptyArticleId: ({ openArticleId }) => [-1, null, undefined].includes(openArticleId),
	},

	actions: {
		isIncludesArticle(articleId: IArticle['id']) {
			return !!this.pureArticles.find((article) => article.id === articleId);
		},

		setArticles(article: IArticle) {
			if (this.isIncludesArticle(article.id)) return;

			this.pureArticles.push(article);
		},

		openSpecialPureArticle(id: IArticle['id']) {
			this.pureArticles.forEach((article) => article.is_open = false);

			const currentArticle = this.pureArticles.find((article) => article.id === id);

			if (!currentArticle) return;

			currentArticle.is_open = true;
		},

		checkOpenPureArticle() {
			const stack = [...this.articles?.results as any];

			while (stack.length > 0) {
				const currentArticle = stack.pop();

				if (!currentArticle) break;

				if (currentArticle.type === 'group') {
					stack.push(...currentArticle.content);
					continue;
				}

				const isFindArticle = this.isIncludesArticle(currentArticle.id);

				if (currentArticle.is_open && !isFindArticle) {
					this.pureArticles.push(currentArticle);
				}
			}

			this.openArticleId = this.pureArticles[0]?.id;
		},

		clearPureArticles() {
			this.pureArticles = [];
		},

		async getArticles(payload: TFetchArticlesPayload) {
			const data = await articleService.fetchArticles(payload);

			if ('error' in data) {
				console.error(data);
				return null;
			}

			if (data?.results?.length === 0) {
				return null;
			}

			this.articles = data;
			this.checkOpenPureArticle();

			return data;
		},

		async getArticleById(id: IArticle['id']) {
			if (this.articleStorage.has(id)) {
				return this.articleStorage.get(id) ?? FallbackArticle;
			}

			const data = await articleService.fetchArticleById(id);

			if ('error' in data) {
				return FallbackArticle;
			}

			this.articleStorage.set(id, data);

			return data;
		},
	},
});
